import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { CaretDownIcon, CaretUpIcon } from '@persona-id/design-tokens/icons';
import { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { Blueprint, BlueprintServerMetadata } from 'dashboard/models/blueprint';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Label, Paragraph } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';

interface ChooseSolutionStepProps {
  blueprintsCatalogServer: BlueprintServerMetadata[];
  previouslyUsedBlueprints: Blueprint[];
  selectedBlueprint: Blueprint;
  setSelectedBlueprint: (selectedBlueprint: Blueprint) => void;
  openBlueprintsQuizModal: () => void;
}

const ChooseSolutionStep = ({
  blueprintsCatalogServer,
  previouslyUsedBlueprints,
  selectedBlueprint,
  setSelectedBlueprint,
  openBlueprintsQuizModal,
}: ChooseSolutionStepProps) => {
  const previouslyUsedBlueprintsFiltered: Blueprint[] = useMemo(() => {
    return previouslyUsedBlueprints
      .filter((previouslyUsedBlueprint) => {
        const blueprintServerMetadata = blueprintsCatalogServer.find(
          (serverMetadata) =>
            serverMetadata.key === previouslyUsedBlueprint['attributes']['source-type'],
        );
        return blueprintServerMetadata && blueprintServerMetadata['is-usable-in-dashboard'];
      })
      .sort((a, b) => a.attributes.name.localeCompare(b.attributes.name));
  }, [previouslyUsedBlueprints, blueprintsCatalogServer]);

  const unarchivedBlueprints: Blueprint[] = useMemo(() => {
    return previouslyUsedBlueprintsFiltered.filter(
      (blueprint) => blueprint.attributes['archived-at'] == null,
    );
  }, [previouslyUsedBlueprintsFiltered]);

  useEffect(() => {
    if (unarchivedBlueprints.length > 0) {
      setSelectedBlueprint(unarchivedBlueprints[0]);
    }
  }, [unarchivedBlueprints]);

  const NUM_PREVIOUSLY_USED_BLUEPRINTS_VISIBLE_DEFAULT = 2;
  const [numPreviouslyUsedBlueprintsVisible, setNumPreviouslyUsedBlueprintsVisible] = useState(
    NUM_PREVIOUSLY_USED_BLUEPRINTS_VISIBLE_DEFAULT,
  );

  if (previouslyUsedBlueprintsFiltered.length === 0) {
    return (
      <>
        <Paragraph color='primary'>
          Choose a starting point based on your business needs. In the next steps, we’ll show you
          how to customize each piece of your solution!
        </Paragraph>
        <Spacer height={2} />
        <Button
          id='home-sandbox-onboarding-checklist__choose-solution-start-quiz'
          width='button'
          onClick={() => openBlueprintsQuizModal()}
        >
          Get recommendations
        </Button>
      </>
    );
  }

  if (unarchivedBlueprints.length === 0) {
    return (
      <>
        <BlueprintCardsWrapper>
          {previouslyUsedBlueprintsFiltered.map((previouslyUsedBlueprint) => (
            <BlueprintCard
              key={`home-sandbox-onboarding-checklist__blueprint-card-${previouslyUsedBlueprint.id}`}
              blueprintsCatalogServer={blueprintsCatalogServer}
              previouslyUsedBlueprint={previouslyUsedBlueprint}
              isSelected={selectedBlueprint && previouslyUsedBlueprint.id === selectedBlueprint.id}
              isSingularBlueprint={previouslyUsedBlueprintsFiltered.length === 1}
              setSelectedBlueprint={setSelectedBlueprint}
            />
          ))}
        </BlueprintCardsWrapper>
        <Paragraph color='primary'>
          It looks like your solutions are no longer active. To proceed, add another recommended
          solution!
        </Paragraph>
        <Spacer height={2} />
        <Button
          id='home-sandbox-onboarding-checklist__choose-solution-start-quiz'
          width='button'
          onClick={() => openBlueprintsQuizModal()}
        >
          Get recommendations
        </Button>
      </>
    );
  }

  return (
    <>
      {previouslyUsedBlueprintsFiltered.length > 1 ? (
        <Paragraph color='primary'>
          Click on the solution you’d like to use throughout this guide. You can come back to this
          step and change this anytime!
        </Paragraph>
      ) : (
        <Paragraph color='primary'>
          This is the solution that you’ll be using throughout this guide:
        </Paragraph>
      )}
      <Spacer height={2} />
      <BlueprintCardsWrapper>
        {previouslyUsedBlueprintsFiltered
          .slice(0, numPreviouslyUsedBlueprintsVisible)
          .map((previouslyUsedBlueprint) => (
            <BlueprintCard
              key={`home-sandbox-onboarding-checklist__blueprint-card-${previouslyUsedBlueprint.id}`}
              blueprintsCatalogServer={blueprintsCatalogServer}
              previouslyUsedBlueprint={previouslyUsedBlueprint}
              isSelected={selectedBlueprint && previouslyUsedBlueprint.id === selectedBlueprint.id}
              isSingularBlueprint={previouslyUsedBlueprintsFiltered.length === 1}
              setSelectedBlueprint={setSelectedBlueprint}
            />
          ))}
      </BlueprintCardsWrapper>
      {previouslyUsedBlueprintsFiltered.length > NUM_PREVIOUSLY_USED_BLUEPRINTS_VISIBLE_DEFAULT && (
        <>
          {numPreviouslyUsedBlueprintsVisible === NUM_PREVIOUSLY_USED_BLUEPRINTS_VISIBLE_DEFAULT ? (
            <BlueprintCardsButton
              onClick={() =>
                setNumPreviouslyUsedBlueprintsVisible(previouslyUsedBlueprintsFiltered.length)
              }
            >
              Show all <CaretDownIcon />
            </BlueprintCardsButton>
          ) : (
            <BlueprintCardsButton
              onClick={() =>
                setNumPreviouslyUsedBlueprintsVisible(
                  NUM_PREVIOUSLY_USED_BLUEPRINTS_VISIBLE_DEFAULT,
                )
              }
            >
              Show fewer <CaretUpIcon />
            </BlueprintCardsButton>
          )}
        </>
      )}
    </>
  );
};

interface BlueprintCardProps {
  blueprintsCatalogServer: BlueprintServerMetadata[];
  previouslyUsedBlueprint: Blueprint;
  isSelected: boolean;
  isSingularBlueprint: boolean; // if true, the clickability CSS effects are removed since there's no list of Blueprints to select from
  setSelectedBlueprint: (selectedBlueprint: Blueprint) => void;
}

const BlueprintCard = ({
  blueprintsCatalogServer,
  previouslyUsedBlueprint,
  isSelected,
  isSingularBlueprint,
  setSelectedBlueprint,
}: BlueprintCardProps) => {
  const blueprintServerMetadata = blueprintsCatalogServer.find(
    (serverMetadata) => serverMetadata.key === previouslyUsedBlueprint['attributes']['source-type'],
  );
  const isArchived = previouslyUsedBlueprint.attributes['archived-at'];

  return (
    <BlueprintCardWrapper
      disabled={isArchived || isSingularBlueprint}
      onClick={() => {
        if (!isArchived) setSelectedBlueprint(previouslyUsedBlueprint);
      }}
    >
      <Card.Body>
        <BlueprintCardHeader>
          <Label size='lg' weight='bold'>
            {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
            {blueprintServerMetadata['display-name']}
          </Label>
          {isSelected && <Sticker variant='blue'>Used for this guide</Sticker>}
          {isArchived && (
            <Sticker
              variant='caution'
              data-marketing-id='home-sandbox-onboarding-checklist__blueprint-archived'
            >
              Limited
            </Sticker>
          )}
        </BlueprintCardHeader>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        <Paragraph color='secondary'>{blueprintServerMetadata.description}</Paragraph>
      </Card.Body>
    </BlueprintCardWrapper>
  );
};

const BlueprintCardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const BlueprintCardWrapper = styled(Card)<{ disabled: boolean; onClick?: () => void }>`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.color.border.primary};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  overflow: hidden;
  transition: ${TransitionDuration.Fast};

  ${({ disabled }) =>
    !disabled &&
    css`
      cursor: pointer;

      &:hover,
      &:focus {
        transform: scale(1.02);
      }
    `};
`;

const BlueprintCardHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const BlueprintCardsButton = styled('button')`
  ${Typography.TextMediumBold};
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${Color.CorePurple700};
  background-color: ${Color.BackgroundPrimary};
  margin-left: ${Spacing[2]};
  cursor: pointer;
`;

export default ChooseSolutionStep;
